import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Container } from 'reactstrap';
import ArchivosService from '../axios_services/archivos.service';
import { AppContext } from '../application/provider';
import { ButtonInsert } from '../components/Buttons/Buttons';
import ModalInduccion from '../components/Modal/ModalInduccion';
import ModalConfirmar from '../components/Modal/ModalConfirmar';
import Loader from '../components/Loader/Loader';
import { Trash2, GripVertical, ChevronLeft, ChevronRight } from 'lucide-react';
import './Prospectos.css';
import './InduccionNegocios.css';

const InduccionNegocios = () => {
  const [globalState] = useContext(AppContext);
  const [images, setImages] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dragIndex, setDragIndex] = useState(null);
  const [dropTargetIndex, setDropTargetIndex] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);
  const [confirmEliminar, setConfirmEliminar] = useState({ isOpen: false, archivoId: null });
  const [exitoSubida, setExitoSubida] = useState({ isOpen: false, cantidad: 0 });

  const perfilId = globalState?.user?.perfil_id;
  const canManage = [6, 8, 23].includes(Number(perfilId));

  const loadImages = useCallback((opts) => {
    setLoading(true);
    ArchivosService.getImagenesInduccion()
      .then((res) => {
        if (res.success) {
          const list = res.data || [];
          setImages(list);
          if (opts && typeof opts.currentIndex === 'number') {
            const ci = opts.currentIndex;
            setCurrentIndex(
              list.length ? Math.max(0, Math.min(ci, list.length - 1)) : 0
            );
          } else {
            setCurrentIndex((prev) => {
              if (!list.length) return 0;
              return prev >= list.length ? list.length - 1 : prev;
            });
          }
        }
      })
      .catch((err) => console.error('Error loading images', err))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadImages();
  }, [loadImages]);

  const persistOrder = async (reordered) => {
    const ordenes = reordered.map((img, i) => ({
      archivo_id: img.archivo_id,
      nuevo_orden: i + 1,
    }));
    if (!ordenes.length) return;
    setSavingOrder(true);
    try {
      const res = await ArchivosService.actualizarOrdenInduccion(ordenes);
      if (!res.success) {
        loadImages();
      }
    } catch {
      loadImages();
    } finally {
      setSavingOrder(false);
    }
  };

  const handleDragStart = (e, index) => {
    if (!canManage || savingOrder) return;
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
  };

  const handleDragOver = (e, index) => {
    if (!canManage || dragIndex === null) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropTargetIndex(index);
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    if (!canManage || dragIndex === null) {
      setDragIndex(null);
      setDropTargetIndex(null);
      return;
    }
    const from = dragIndex;
    setDragIndex(null);
    setDropTargetIndex(null);
    if (from === dropIndex) return;

    const currentArchivoId = images[currentIndex]?.archivo_id;
    const reordered = [...images];
    const [removed] = reordered.splice(from, 1);
    reordered.splice(dropIndex, 0, removed);
    const withOrder = reordered.map((img, i) => ({ ...img, origen_id: i + 1 }));
    setImages(withOrder);

    const newIdx = withOrder.findIndex((i) => i.archivo_id === currentArchivoId);
    setCurrentIndex(newIdx >= 0 ? newIdx : 0);

    await persistOrder(withOrder);
  };

  const handleDragEnd = () => {
    setDragIndex(null);
    setDropTargetIndex(null);
  };

  const ejecutarEliminarImagen = async () => {
    const archivoId = confirmEliminar.archivoId;
    setConfirmEliminar({ isOpen: false, archivoId: null });
    if (!archivoId) return;

    const idx = images.findIndex((i) => i.archivo_id === archivoId);
    const nextLen = images.length - 1;
    let nextIndex = currentIndex;
    if (nextLen <= 0) nextIndex = 0;
    else if (idx < currentIndex) nextIndex = currentIndex - 1;
    else if (idx === currentIndex) nextIndex = Math.min(idx, nextLen - 1);
    else nextIndex = Math.min(currentIndex, nextLen - 1);

    try {
      const res = await ArchivosService.eliminarImagenInduccion(archivoId);
      if (!res.success) {
        alert(res.message || 'Error al eliminar');
        return;
      }
      const next = images.filter((i) => i.archivo_id !== archivoId);
      const withOrder = next.map((img, i) => ({ ...img, origen_id: i + 1 }));
      if (withOrder.length) {
        await persistOrder(withOrder);
      }
      loadImages({ currentIndex: nextIndex });
    } catch (err) {
      console.error(err);
      alert('Error al eliminar la imagen');
      loadImages();
    }
  };

  const goPrev = () => setCurrentIndex((i) => Math.max(0, i - 1));
  const goNext = () => setCurrentIndex((i) => Math.min(images.length - 1, i + 1));

  return (
    <Container className="prospectos">
      <div className="encabezado">
        <h1>MANUAL DE INDUCCIÓN</h1>
      </div>

      <div className="induccion-presentacion-wrap">
        <div className="induccion-toolbar">
          {canManage && (
            <ButtonInsert onClick={() => setIsModalOpen(true)} />
          )}
        </div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '40px' }}>
            <Loader />
          </div>
        ) : images.length > 0 ? (
          <div className="induccion-presentacion">
            <div
              className="induccion-carrusel"
              onDragLeave={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget)) setDropTargetIndex(null);
              }}
            >
              {images.map((img, idx) => (
                <div
                  key={img.archivo_id}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setCurrentIndex(idx);
                    }
                  }}
                  className={[
                    'induccion-carrusel-item',
                    idx === currentIndex ? 'induccion-carrusel-item--active' : '',
                    canManage ? 'induccion-carrusel-item--draggable' : '',
                    dropTargetIndex === idx ? 'induccion-carrusel-item--dragover' : '',
                  ].filter(Boolean).join(' ')}
                  onClick={() => setCurrentIndex(idx)}
                  draggable={canManage && !savingOrder}
                  onDragStart={(e) => handleDragStart(e, idx)}
                  onDragOver={(e) => handleDragOver(e, idx)}
                  onDrop={(e) => handleDrop(e, idx)}
                  onDragEnd={handleDragEnd}
                >
                  {canManage && (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        paddingTop: 4,
                        color: '#9ca3af',
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <GripVertical size={14} />
                    </div>
                  )}
                  <img
                    src={img.url_completa}
                    alt=""
                    className="induccion-carrusel-thumb"
                    draggable={false}
                    onContextMenu={(e) => e.preventDefault()}
                  />
                  <div className="induccion-carrusel-meta">
                    <span>{idx + 1}</span>
                    {canManage && (
                      <button
                        type="button"
                        className="induccion-carrusel-del"
                        title="Eliminar"
                        onClick={(e) => {
                          e.stopPropagation();
                          setConfirmEliminar({ isOpen: true, archivoId: img.archivo_id });
                        }}
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="induccion-stage">
              <div className="induccion-stage-view">
                <button
                  type="button"
                  className="induccion-stage-nav"
                  aria-label="Imagen anterior"
                  disabled={currentIndex <= 0}
                  onClick={goPrev}
                >
                  <ChevronLeft size={22} />
                </button>
                <div className="induccion-stage-img-wrap">
                  <img
                    src={images[currentIndex].url_completa}
                    alt={`Imagen ${currentIndex + 1}`}
                    className="induccion-stage-img"
                    onContextMenu={(e) => e.preventDefault()}
                    draggable={false}
                  />
                </div>
                <button
                  type="button"
                  className="induccion-stage-nav"
                  aria-label="Imagen siguiente"
                  disabled={currentIndex >= images.length - 1}
                  onClick={goNext}
                >
                  <ChevronRight size={22} />
                </button>
              </div>
              <div className="induccion-stage-footer">
                Imagen {currentIndex + 1} de {images.length}
              </div>
            </div>
          </div>
        ) : (
          <p style={{ textAlign: 'center', marginTop: '40px', color: '#666' }}>
            No hay imágenes de presentación disponibles.
          </p>
        )}
      </div>

      <ModalConfirmar
        isOpen={confirmEliminar.isOpen}
        titulo="Advertencia"
        mensaje="¿Eliminar esta imagen? No podrá revertir esta acción."
        textConfirmar="Eliminar"
        textCancelar="Cancelar"
        variante="peligro"
        onConfirmar={ejecutarEliminarImagen}
        onCancelar={() => setConfirmEliminar({ isOpen: false, archivoId: null })}
      />

      <ModalConfirmar
        isOpen={exitoSubida.isOpen}
        titulo="Confirmación"
        mensaje={
          exitoSubida.cantidad === 1
            ? 'Se subió 1 imagen correctamente.'
            : `Se subieron ${exitoSubida.cantidad} imágenes correctamente.`
        }
        textConfirmar="Aceptar"
        textCancelar="Cerrar"
        variante="exito"
        onConfirmar={() => setExitoSubida({ isOpen: false, cantidad: 0 })}
        onCancelar={() => setExitoSubida({ isOpen: false, cantidad: 0 })}
      />

      {isModalOpen && (
        <ModalInduccion
          closeModal={() => setIsModalOpen(false)}
          images={images}
          refreshImages={loadImages}
          onUploadSuccess={(cantidad) => setExitoSubida({ isOpen: true, cantidad })}
        />
      )}
    </Container>
  );
};

export default InduccionNegocios;
